import { FC, useContext, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Table } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Column } from 'react-table';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { EPaymentItemType, TShopGood } from 'types';

import { IGoodsFieldProps } from './interfaces';
import { GOODS, IGoodsRequest, IGoodsResponse } from './schema';
import s from './ShopGoodsTable.module.scss';

export const ShopGoodsTable: FC<IGoodsFieldProps> = ({
  onItemSelect,
  clientUUID,
  search,
  combo,
  disabledTariff,
  promoCode,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const columns: Array<Column<TShopGood>> = useMemo(
    () => [
      {
        Header: t('title'),
        accessor: 'title',
        width: 200,
      },
      {
        Header: t('group'),
        id: 'category',
        accessor: ({ category }) => (category?.title ? category.title : false),
        Cell: ({ value }: { value: string }) => {
          return (
            <div className={s.column_category_block}>
              <span className={cn(s.text, value && s.active)}>
                {value ? value : t('goods.table.category_empty')}
              </span>
            </div>
          );
        },
        width: 184,
      },
      {
        Header: t('amount'),
        accessor: 'amount',
        Cell: ({ value }) => `${value.toLocaleString()} ${t('pcs')}`,
        width: 154,
      },
      {
        Header: t('cost'),
        accessor: 'cost',
        Cell: ({ value }) => `${value.toLocaleString()} ${currency.sign}`,
        width: 122,
      },
    ],
    [t]
  );

  const { loading, data } = useQuery<IGoodsResponse, IGoodsRequest>(GOODS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        client_uuid: clientUUID ?? undefined,
      },
    },
  });

  const filteredData = useMemo(
    () =>
      data?.goods.filter((good) => {
        if (
          (search &&
            !good.title.toLowerCase().includes(search.toLowerCase())) ||
          !good?.available_to_buy
        ) {
          return false;
        }

        return good.amount;
      }) ?? [],
    [search, data]
  );

  return (
    <Table
      disabled={!!combo || disabledTariff || promoCode}
      className={s['services-table']}
      hiddenColumns={['id']}
      data={filteredData}
      columns={columns}
      onRowClick={(_, row) => {
        const { cost, id, use_global_discounts } = row.original;

        onItemSelect({
          id,
          cost,
          use_global_discounts,
          amount: 1,
          type: EPaymentItemType.GOOD,
          original: row.original,
        });
      }}
      isLoading={loading}
      emptyDataPlaceholder={t('shopForm.empty.goods')}
      dataTestId="goods-table"
    />
  );
};
